import React, { Component } from "react";
import Form from "react-bootstrap/Form";
import delivaApiService from "../../../common/services/apiService";

class ParcelCenterLocationInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: props.value ? props.value.label : "",
      centers: [],
      predictions: [],
      loading: false,
      showOptions: false,
      selectionError: "",
    };
    this.wrapperRef = React.createRef();
    this.requestId = 0;
    this.searchTimer = null;
    this.autocompleteService = null;
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.countryCode !== this.props.countryCode ||
      prevProps.stateName !== this.props.stateName ||
      prevProps.city !== this.props.city
    ) {
      this.requestId += 1;
      clearTimeout(this.searchTimer);
      this.setState({
        centers: [],
        predictions: [],
        loading: false,
        showOptions: false,
        selectionError: "",
      });
    }

    if (prevProps.value?.label !== this.props.value?.label) {
      this.setState({ value: this.props.value ? this.props.value.label : "" });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    this.requestId += 1;
    clearTimeout(this.searchTimer);
  }

  handleClickOutside = (event) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({ showOptions: false });
    }
  };

  search = (value) => {
    clearTimeout(this.searchTimer);
    const requestId = ++this.requestId;
    const query = value.trim();
    if (query.length < 2) {
      this.setState({ centers: [], predictions: [], loading: false });
      return;
    }

    this.setState({ loading: true, showOptions: true });
    this.searchTimer = setTimeout(() => {
      delivaApiService("searchParcelCenters", {
        city: encodeURIComponent(this.props.city || ""),
        state: encodeURIComponent(this.props.stateName || ""),
        query: encodeURIComponent(query),
      })
        .then((res) => {
          if (requestId !== this.requestId) return;
          const results = Array.isArray(res?.data?.resourceData)
            ? res.data.resourceData
            : [];
          const centers = results.filter(this.matchesParcelCenterArea);
          this.setState({ centers, loading: false });
        })
        .catch(() => {
          if (requestId === this.requestId) {
            this.setState({ centers: [], loading: false });
          }
        });
    }, 250);

    if (!window.google?.maps?.places) {
      this.setState({ predictions: [] });
      return;
    }
    if (!this.autocompleteService) {
      this.autocompleteService =
        new window.google.maps.places.AutocompleteService();
    }
    this.autocompleteService.getPlacePredictions(
      {
        input: [query, this.props.city, this.props.stateName]
          .filter(Boolean)
          .join(", "),
        ...(this.props.countryCode
          ? {
              componentRestrictions: {
                country: this.props.countryCode.toLowerCase(),
              },
            }
          : {}),
      },
      (predictions, status) => {
        if (requestId !== this.requestId) return;
        const ok = window.google.maps.places.PlacesServiceStatus.OK;
        this.setState({
          predictions:
            status === ok && predictions
              ? predictions.filter((prediction) =>
                  this.matchesSelectedAreaText(prediction.description),
                )
              : [],
        });
      },
    );
  };

  handleChange = (event) => {
    const value = event.target.value;
    this.setState({ value, showOptions: true, selectionError: "" });
    this.props.onChange({
      label: value,
      lat: null,
      lng: null,
      parcelCenterId: null,
    });
    this.search(value);
  };

  selectCenter = (center) => {
    const label = center.name || center.locationAddress || "Parcel center";
    const lat = Number(center.locationLat);
    const lng = Number(center.locationLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    this.setState({ value: label, showOptions: false });
    this.props.onChange({
      label,
      lat,
      lng,
      parcelCenterId: center.parcelCenterId,
    });
  };

  selectPrediction = (prediction) => {
    const service = new window.google.maps.places.PlacesService(
      document.createElement("div"),
    );
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["geometry", "formatted_address", "name", "address_components"],
      },
      (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place?.geometry?.location
        ) {
          return;
        }
        const components = place.address_components || [];
        const getComponentNames = (types) =>
          components
            .filter((component) =>
              component.types.some((type) => types.includes(type)),
            )
            .flatMap((component) => [component.long_name, component.short_name])
            .map((name) => this.normalizeLocationName(name));
        const selectedCountry = this.normalizeLocationName(
          this.props.countryCode,
        );
        const selectedState = this.normalizeLocationName(this.props.stateName);
        const selectedCity = this.normalizeLocationName(this.props.city);
        const countryMatches =
          !selectedCountry ||
          getComponentNames(["country"]).includes(selectedCountry);
        const stateMatches =
          !selectedState ||
          getComponentNames(["administrative_area_level_1"]).includes(
            selectedState,
          );
        const cityMatches =
          !selectedCity ||
          getComponentNames([
            "locality",
            "postal_town",
            "administrative_area_level_2",
            "administrative_area_level_3",
            "sublocality",
            "sublocality_level_1",
          ]).includes(selectedCity);

        if (!countryMatches || !stateMatches || !cityMatches) {
          this.setState({
            selectionError:
              "Choose a location within the selected country, state, and city.",
            showOptions: false,
          });
          return;
        }

        const location = place.geometry.location;
        const label =
          place.name || place.formatted_address || prediction.description;
        this.setState({ value: label, showOptions: false, selectionError: "" });
        this.props.onChange({
          lat: location.lat(),
          lng: location.lng(),
          label,
        });
      },
    );
  };

  normalizeLocationName = (name) =>
    String(name || "")
      .trim()
      .toLocaleLowerCase()
      .replace(/\s+/g, " ");

  matchesSelectedAreaText = (text) => {
    const normalizedText = this.normalizeLocationName(text);
    const city = this.normalizeLocationName(this.props.city);
    const state = this.normalizeLocationName(this.props.stateName);
    return (
      Boolean(city && state) &&
      normalizedText.includes(city) &&
      normalizedText.includes(state)
    );
  };

  matchesParcelCenterArea = (center) => {
    const city = this.normalizeLocationName(this.props.city);
    const state = this.normalizeLocationName(this.props.stateName);
    if (!city || !state) return false;

    const centerCity = this.normalizeLocationName(
      center.city || center.cityName || center.locationCity || center.town,
    );
    const centerState = this.normalizeLocationName(
      center.state ||
        center.stateName ||
        center.locationState ||
        center.province ||
        center.region,
    );
    const address = this.normalizeLocationName(
      center.locationAddress ||
        center.address ||
        center.fullAddress ||
        center.formattedAddress,
    );

    return (
      (centerCity === city || (!centerCity && address.includes(city))) &&
      (centerState === state || (!centerState && address.includes(state)))
    );
  };

  render() {
    const { label, disabled } = this.props;
    const {
      value,
      centers,
      predictions,
      loading,
      showOptions,
      selectionError,
    } = this.state;
    return (
      <Form.Group
        className="custom-form position-relative"
        ref={this.wrapperRef}
      >
        <Form.Label className="color-head">{label}</Form.Label>
        <Form.Control
          type="text"
          className="py-0"
          value={value}
          onChange={this.handleChange}
          onFocus={() => this.setState({ showOptions: true })}
          disabled={disabled}
          placeholder={`Search ${label.toLowerCase()}`}
          autoComplete="off"
        />
        {selectionError && (
          <div className="text-danger small mt-1">{selectionError}</div>
        )}
        {showOptions &&
          (loading || centers.length > 0 || predictions.length > 0) && (
            <ul
              className="dropdown-menu show w-100 position-absolute"
              style={{ maxHeight: "220px", overflowY: "auto", zIndex: 1050 }}
            >
              {loading && (
                <li className="dropdown-item text-muted">Searching...</li>
              )}
              {centers.map((center) => (
                <li
                  key={`center-${center.parcelCenterId}`}
                  className="dropdown-item"
                  style={{ cursor: "pointer" }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => this.selectCenter(center)}
                >
                  {center.name}{" "}
                  <small className="text-muted">Parcel center</small>
                </li>
              ))}
              {predictions.map((prediction) => (
                <li
                  key={prediction.place_id}
                  className="dropdown-item"
                  style={{ cursor: "pointer" }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => this.selectPrediction(prediction)}
                >
                  {prediction.description}
                </li>
              ))}
            </ul>
          )}
      </Form.Group>
    );
  }
}

export default ParcelCenterLocationInput;
